import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { get } from "lodash";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { XIcon } from "lucide-react";
import { toast } from "sonner";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { formatError } from "helpers/formatError";
import { useDebounce } from "hooks/useDebounce";

import { REQUIRED_MARK } from "./fieldClasses";

const SEARCH_DEBOUNCE_MS = 300;
const CREATE_VALUE = "__create__";

// Prefixed with the record id so similarly named records stay distinguishable.
const formatRecordLabel = (id, name) =>
  name ? `[ID: ${id}] ${name}` : `[ID: ${id}]`;

const OptionsSkeleton = () => (
  <div className="tw:flex tw:flex-col tw:p-2" aria-hidden>
    <Skeleton className="tw:h-5 tw:rounded-xs tw:my-1 tw:w-full" />
    <Skeleton className="tw:h-5 tw:rounded-xs tw:my-1 tw:w-full" />
    <Skeleton className="tw:h-5 tw:rounded-xs tw:my-1 tw:w-full" />
  </div>
);

/**
 * Async single-record picker for relationship fields. Options come from
 * `fetchOptions(search)`; the field value is the raw record id, or null when
 * cleared. `display` seeds the current value's label in edit mode, `onCreate`
 * adds an inline "Create '<text>'" item, and `optionsKey` scopes the options
 * cache for field names that resolve to different targets.
 */
const FormRecordPicker = ({
  name,
  label,
  required,
  fetchOptions,
  optionsKey,
  onCreate,
  display,
  placeholder,
  disabled,
  readOnly,
  id,
  onValueChange,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;
  const queryClient = useQueryClient();
  const queryKey = ["record-picker", optionsKey ?? name];

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);

  // Wrapped so onCreate receives only the text, not react-query's mutate context.
  const createMutation = useMutation({ mutationFn: text => onCreate(text) });

  const {
    data,
    isFetching,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: [...queryKey, debouncedSearch],
    queryFn: () => fetchOptions(debouncedSearch || null),
    // Gated on the popup so a form of many pickers issues no fetch until one is
    // opened; a preselected value still renders from the `display` seed.
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const fetched = useMemo(
    () =>
      (Array.isArray(data) ? data : []).map(r => ({
        value: r.id,
        name: r.name,
        label: formatRecordLabel(r.id, r.name),
      })),
    [data]
  );

  const seedItem = useMemo(
    () =>
      display && display.id != null
        ? {
            value: display.id,
            label:
              formatRecordLabel(display.id, display.label) +
              (display.deleted ? " (deleted)" : ""),
          }
        : null,
    [display]
  );

  const resolveLabel = value => {
    const known =
      (picked?.value === value ? picked : null) ??
      (seedItem?.value === value ? seedItem : null) ??
      fetched.find(it => it.value === value);
    return known?.label ?? formatRecordLabel(value);
  };

  const errorMessage = isError
    ? formatError(queryError, "Could not load options")
    : null;
  const isBusy = isFetching || isError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedItem =
          field.value == null
            ? null
            : { value: field.value, label: resolveLabel(field.value) };

        const items = [...fetched];
        const hasExactMatch = fetched.some(
          it => (it.name ?? "").toLowerCase() === search.toLowerCase()
        );
        if (onCreate && search && !hasExactMatch) {
          items.push({ value: CREATE_VALUE, label: `Create "${search}"` });
        }

        const create = () => {
          if (createMutation.isPending) return;
          createMutation.mutate(search, {
            onSuccess: record => {
              if (record?.id != null) {
                setPicked({
                  value: record.id,
                  label: formatRecordLabel(record.id, record.name ?? search),
                });
                field.onChange(record.id);
                onValueChange?.(record.id);
                setSearch("");
                queryClient.invalidateQueries({ queryKey });
              }
            },
            onError: err =>
              toast.error(formatError(err, "Could not create record")),
          });
        };

        const handleValueChange = next => {
          if (onCreate && next?.value === CREATE_VALUE) {
            create();
            return;
          }
          setPicked(next);
          field.onChange(next?.value ?? null);
          onValueChange?.(next?.value ?? null);
        };

        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <Combobox
              items={items}
              value={selectedItem}
              onValueChange={handleValueChange}
              onInputValueChange={setSearch}
              open={open}
              // Base UI lets a readOnly combobox open for browsing; this field must stay closed.
              onOpenChange={next => setOpen(next && !readOnly)}
              itemToStringLabel={item => item?.label ?? ""}
              isItemEqualToValue={(a, b) => a?.value === b?.value}
              filter={null}
              disabled={disabled}
              readOnly={readOnly}
            >
              <div className="tw:group/record-picker tw:relative tw:w-full">
                <ComboboxTrigger
                  id={inputId}
                  aria-required={required || undefined}
                  aria-invalid={error ? true : undefined}
                  onBlur={field.onBlur}
                  onKeyDown={e => {
                    if (
                      (e.key === "Backspace" || e.key === "Delete") &&
                      field.value != null &&
                      !readOnly &&
                      !disabled
                    ) {
                      e.preventDefault();
                      setPicked(null);
                      field.onChange(null);
                      onValueChange?.(null);
                    }
                  }}
                  className={buttonVariants({
                    variant: "outline",
                    className:
                      "tw:w-full tw:justify-between tw:bg-transparent tw:font-normal tw:group-has-data-[slot=combobox-clear]/record-picker:[&>svg]:invisible tw:data-readonly:cursor-default tw:data-readonly:bg-muted tw:dark:bg-input/30",
                  })}
                >
                  <ComboboxValue>
                    {selected =>
                      selected ? (
                        <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left tw:text-foreground">
                          {selected.label}
                        </span>
                      ) : (
                        <span className="tw:flex-1 tw:text-left tw:text-muted-foreground">
                          {placeholder ? placeholder : "Search..."}
                        </span>
                      )
                    }
                  </ComboboxValue>
                </ComboboxTrigger>
                <ComboboxPrimitive.Clear
                  data-slot="combobox-clear"
                  aria-label="Clear selection"
                  render={<Button variant="ghost" size="icon-sm" />}
                  className="tw:absolute tw:top-1/2 tw:right-1.5 tw:-translate-y-1/2 tw:text-muted-foreground"
                >
                  <XIcon className="tw:pointer-events-none" />
                </ComboboxPrimitive.Clear>
              </div>
              <ComboboxContent>
                <ComboboxInput
                  showTrigger={false}
                  placeholder={
                    placeholder ??
                    (onCreate ? "Search or create..." : "Search...")
                  }
                />
                <ComboboxPrimitive.Status className="tw:sr-only">
                  {isFetching ? "Searching…" : null}
                </ComboboxPrimitive.Status>
                {errorMessage && (
                  <div className="tw:px-2 tw:py-1.5 tw:text-sm tw:text-muted-foreground">
                    {errorMessage}
                  </div>
                )}
                {isFetching && <OptionsSkeleton />}
                <ComboboxEmpty>{isBusy ? null : "No matches"}</ComboboxEmpty>
                <ComboboxList className={isBusy ? "tw:hidden" : undefined}>
                  {item => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormRecordPicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  fetchOptions: PropTypes.func.isRequired,
  optionsKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreate: PropTypes.func,
  display: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    deleted: PropTypes.bool,
  }),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  onValueChange: PropTypes.func,
};

export default FormRecordPicker;
